// Single source of truth for the resignation state machine described in the HLD.
// The backend payload uses inconsistent casing (NoticePeriodDays, ApprovedDate,
// reviewedDcoumentsDate) so every component reads through normalizeResignation()
// and never touches the raw response shape directly.

export const RESIGNATION_STATUS = {
  IDLE: "IDLE",
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  UPLOADED: "UPLOADED",
  COMPLETED: "COMPLETED",
};

export const RESIGNATION_STATUS_META = {
  IDLE: {
    label: "Not Applied",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
  PENDING: {
    label: "Pending Approval",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  APPROVED: {
    label: "Approved",
    className: "bg-blue-secondery-1 text-blue-600 border-blue-secondery",
  },
  UPLOADED: {
    label: "Documents Under Review",
    className: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  COMPLETED: {
    label: "Completed",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
};

// Progress rail shown to the employee. Each step is "done" once the record has
// moved past it.
export const FLOW_STEPS = [
  { key: RESIGNATION_STATUS.PENDING, label: "Resignation Submitted" },
  { key: RESIGNATION_STATUS.APPROVED, label: "Approved by HR" },
  { key: RESIGNATION_STATUS.UPLOADED, label: "Exit Documents Submitted" },
  { key: RESIGNATION_STATUS.COMPLETED, label: "Documents Verified" },
];

export const STEP_ORDER = [
  RESIGNATION_STATUS.IDLE,
  RESIGNATION_STATUS.PENDING,
  RESIGNATION_STATUS.APPROVED,
  RESIGNATION_STATUS.UPLOADED,
  RESIGNATION_STATUS.COMPLETED,
];

export const QUEUE_FILTERS = [
  { value: "ALL", label: "All" },
  { value: RESIGNATION_STATUS.PENDING, label: "Pending" },
  { value: RESIGNATION_STATUS.APPROVED, label: "Approved" },
  { value: RESIGNATION_STATUS.UPLOADED, label: "Docs to Review" },
  { value: RESIGNATION_STATUS.COMPLETED, label: "Completed" },
];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // matches the rest of the app

// --- Last working day -------------------------------------------------------
// All of this is date-only maths done in *local* time on purpose. Building the
// value with `new Date().toISOString()` would shift an IST afternoon back to the
// previous UTC day and hand the backend a last working day one day short, so
// dates are always parsed and formatted from local Y/M/D parts. Parsing is
// explicit for the same reason: `new Date("2026-09-19")` is UTC midnight, which
// is the *previous* calendar day for anyone west of Greenwich.
const parseDateOnly = (value) => {
  // `new Date(null)` is the epoch, not an invalid date - without this guard a
  // missing date renders as 01 Jan 1970 in a date input.
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }
  if (typeof value === "string") {
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (parts) {
      // Midday keeps setDate() arithmetic clear of DST boundaries.
      return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12, 0, 0, 0);
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Formats a date for an <input type="date">, which only ever accepts YYYY-MM-DD.
export const toDateInputValue = (value) => {
  const date = parseDateOnly(value);
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const addDays = (value, days) => {
  const date = parseDateOnly(value);
  if (!date) return null;
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + Number(days || 0));
  return date;
};

// Whole calendar days from `a` to `b` (negative when `b` is earlier).
export const daysBetween = (a, b) => {
  const from = parseDateOnly(a);
  const to = parseDateOnly(b);
  if (!from || !to) return null;
  from.setHours(12, 0, 0, 0);
  to.setHours(12, 0, 0, 0);
  return Math.round((to - from) / 86400000);
};

// `noticePeriodDays` can legitimately be 0 - notice waived, employee leaves the
// same day - so it must never be tested for truthiness. `days ? x : y` takes the
// "missing" branch for a real 0 and the field renders as "-". These two return
// null *only* when the value is genuinely absent, so 0 and absent stay distinct.
export const getNoticeDays = (record) => {
  const raw = record?.noticePeriodDays;
  if (raw === null || raw === undefined || raw === "") return null;
  const days = Number(raw);
  return Number.isFinite(days) && days >= 0 ? days : null;
};

export const formatNoticePeriod = (record) => {
  const days = getNoticeDays(record);
  if (days === null) return "-";
  return `${days} ${days === 1 ? "day" : "days"}`;
};

// Approval date + the notice period the employee owes. `noticePeriodDays` comes
// from the employee's onboarding record via the resignation payload; when it is
// missing or zero this returns the approval date itself, so the picker still
// opens on a real date and the reviewer is prompted to set one.
export const computeLastWorkingDay = (noticePeriodDays, fromDate = new Date()) => {
  const days = Number(noticePeriodDays);
  return addDays(fromDate, Number.isFinite(days) && days > 0 ? days : 0);
};

export const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  // `lastWorkingDay` arrives as a bare YYYY-MM-DD, which `new Date()` reads as UTC
  // midnight - that renders as the *previous* day anywhere west of Greenwich, so
  // date-only values go through the local-time parser instead. Values that carry a
  // time are left alone so `withTime` still shows the real clock time.
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseDateOnly(value) : new Date(value);
  // The contract carries placeholder strings like "date" in a few places, and a
  // half-filled record can hold anything - never render "Invalid Date".
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  const date = parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  if (!withTime) return date;
  return `${date}, ${parsed.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

// The record is closed either when the backend moves it to COMPLETED/CLOSED, or
// - if it keeps the status at UPLOADED - when the document review date is set.
export const isFlowClosed = (record) => {
  if (!record) return false;
  if (record.status === RESIGNATION_STATUS.COMPLETED || record.status === "CLOSED") return true;
  return record.status === RESIGNATION_STATUS.UPLOADED && Boolean(record.reviewedDocumentsDate);
};

export const effectiveStatus = (record) =>
  isFlowClosed(record) ? RESIGNATION_STATUS.COMPLETED : record?.status || RESIGNATION_STATUS.IDLE;

// Removing an employee is gated on two things, not one: the exit clearance has to
// be closed *and* the last working day has to have passed. The employee still owes
// work up to and including that date, so deactivating earlier locks them out of a
// day they are still employed for. A record with no last working day keeps the gate
// shut - fail closed, the same stance the payroll month locks take.
export const canRemoveEmployee = (record) => {
  if (!isFlowClosed(record)) return false;
  const daysUntilLastWorkingDay = daysBetween(new Date(), record?.lastWorkingDay);
  return daysUntilLastWorkingDay !== null && daysUntilLastWorkingDay < 0;
};

// Every endpoint answers with { data, message, status: "success" }. That outer
// `status` is a transport result, not a resignation status, so it must never be
// mistaken for one - the envelope is identified by the record fields being one
// level down (`employeeId` missing on the outer object).
const unwrapEnvelope = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  if (raw.employeeId === undefined && raw.data && typeof raw.data === "object") {
    return raw.data;
  }
  return raw;
};

export const normalizeResignation = (raw) => {
  if (!raw) return null;
  const record = unwrapEnvelope(raw);
  if (!record || typeof record !== "object") return null;

  const documentsNode = record.documents;
  const isDocsArray = Array.isArray(documentsNode);
  const nestedDocsRejection = isDocsArray ? null : documentsNode?.rejectionReason ?? null;
  const status = String(record.status || RESIGNATION_STATUS.IDLE).toUpperCase();

  return {
    id: record._id ?? record.id ?? null,
    employeeId: record.employeeId ?? null,
    employeeName: record.employeeName ?? "",
    employeeNum: record.employeeNum ?? "",
    status,
    reason: record.reason ?? "",
    noticePeriodDays: record.NoticePeriodDays ?? record.noticePeriodDays ?? null,
    submittedDate: record.submittedDate ?? null,
    approvedDate: record.ApprovedDate ?? record.approvedDate ?? null,
    reviewedDocumentsDate: record.reviewedDcoumentsDate ?? record.reviewedDocumentsDate ?? null,
    lastWorkingDay: record.lastWorkingDay ?? null,
    // Set when HR rejects the resignation request itself (record goes back to IDLE).
    rejectionReason: record.rejectionReason ?? null,
    // Set when HR rejects the uploaded exit documents (record goes back to APPROVED).
    // Some responses keep `documents` flat, in which case the reason arrives on the
    // top-level field instead - at APPROVED that can only be a document rejection.
    documentsRejectionReason:
      nestedDocsRejection ??
      (status === RESIGNATION_STATUS.APPROVED ? record.rejectionReason ?? null : null),
    documents: isDocsArray ? documentsNode : documentsNode?.documents ?? [],
    submittedBy: record.submittedBy ?? null,
    submittedByName: record.submittedByName ?? null,
    submittedByRole: record.submittedByRole ?? null,
    withdrawnBy: record.withdrawnBy ?? null,
    withdrawnByName: record.withdrawnByName ?? null,
    withdrawnByRole: record.withdrawnByRole ?? null,
    withdrawnOn: record.withdrawnOn ?? null,
    documentsUploadedBy: record.documentsUploadedBy ?? null,
    documentsUploadedByName: record.documentsUploadedByName ?? null,
    documentsUploadedByRole: record.documentsUploadedByRole ?? null,
    documentsUploadedOn: record.documentsUploadedOn ?? null,
  };
};

export const normalizeResignationList = (raw) => {
  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return list.map(normalizeResignation).filter(Boolean);
};

// RTK Query surfaces failures as { status, data } - dig out whatever the backend
// put in `message` before falling back to something generic.
export const apiErrorMessage = (error, fallback = "Something went wrong. Please try again.") =>
  error?.data?.message || error?.data?.error || error?.error || fallback;
